import React, { useState, useEffect } from 'react';
import { auth, db } from './firebase-config';
import { collection, query, where, getDocs, getDoc, doc, deleteDoc, updateDoc } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import './Applicants.css';

const Applicants = () => {
  const navigate = useNavigate();
  const [jobPostings, setJobPostings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedJobs, setExpandedJobs] = useState(new Set());
  const [searchQuery, setSearchQuery] = useState('');
  const [conversationEmployeeIds, setConversationEmployeeIds] = useState(new Set());
  const [selectedJob, setSelectedJob] = useState(null);
  const [showJobModal, setShowJobModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editedJob, setEditedJob] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    const fetchJobPostings = async () => {
      const user = auth.currentUser;
      console.log('Current user:', user?.uid); // Log the user ID

      if (!user) {
        console.log('No user found, redirecting to login');
        navigate('/login');
        return;
      }

      try {
        // Fetch all conversations for this employer
        const conversationsQuery = query(
          collection(db, 'conversations'),
          where('employerId', '==', user.uid)
        );
        const conversationsSnapshot = await getDocs(conversationsQuery);
        const employeeIdsWithConversation = new Set(
          conversationsSnapshot.docs.map(doc => doc.data().employeeId)
        );
        setConversationEmployeeIds(employeeIdsWithConversation);

        // Print all jobs for the employer
        const jobsQuery = query(
          collection(db, 'jobs'),
          where('employerId', '==', user.uid)
        );
        const jobsSnapshot = await getDocs(jobsQuery);
        const jobsList = jobsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        console.log('DEBUG: Jobs for employer', user.uid, jobsList);
        // Print all applications in the database
        const allApplicationsSnapshot = await getDocs(collection(db, 'applications'));
        const allApplicationsList = allApplicationsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        console.log('DEBUG: All applications in database', allApplicationsList);
        // For each job, fetch applications with jobId == jobDoc.id
        const jobPostingsList = await Promise.all(
          jobsSnapshot.docs.map(async (jobDoc) => {
            const jobData = jobDoc.data();
            const applicationsQuery = query(
              collection(db, 'applications'),
              where('jobId', '==', jobDoc.id)
            );
            const applicationsSnapshot = await getDocs(applicationsQuery);
            const applications = await Promise.all(
              applicationsSnapshot.docs.map(async (applicationDoc) => {
                const applicationData = applicationDoc.data();
                let employeeDoc, employeeData;
                try {
                  employeeDoc = await getDoc(doc(db, 'employees', applicationData.employeeId));
                  employeeData = employeeDoc.data();
                } catch (e) {
                  employeeData = null;
                }
                return {
                  id: applicationDoc.id,
                  ...applicationData,
                  employeeName: employeeData?.fullName || applicationData.employeeId || 'Unknown',
                  resumeUrl: employeeData?.resumeUrl || '',
                  jobTitle: jobData.title || '',
                  employeeId: applicationData.employeeId,
                  status: applicationData.status || 'pending',
                };
              })
            );
            return {
              id: jobDoc.id,
              ...jobData,
              applications
            };
          })
        );
        setJobPostings(jobPostingsList);
        setExpandedJobs(new Set());
      } catch (error) {
        console.error('Error fetching job postings:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchJobPostings();
  }, [navigate]);

  // Filter applicants by search query
  const filteredJobPostings = jobPostings.map(job => ({
    ...job,
    applications: job.applications.filter(app => {
      const q = searchQuery.toLowerCase();
      return (
        app.employeeName?.toLowerCase().includes(q) ||
        app.jobTitle?.toLowerCase().includes(q) ||
        app.resumeUrl?.toLowerCase().includes(q)
      );
    })
  }));

  const toggleJobExpansion = (jobId) => {
    setExpandedJobs(prevExpandedJobs => {
      const newExpandedJobs = new Set(prevExpandedJobs);
      if (newExpandedJobs.has(jobId)) {
        newExpandedJobs.delete(jobId);
      } else {
        newExpandedJobs.add(jobId);
      }
      return newExpandedJobs;
    });
  };

  const openJobDetails = (job) => {
    setSelectedJob(job);
    setEditedJob({ ...job });
    setShowJobModal(true);
    setIsEditing(false);
  };

  const closeJobModal = () => {
    setShowJobModal(false);
    setSelectedJob(null);
    setEditedJob(null);
    setIsEditing(false);
  };

  const handleEditClick = () => {
    setIsEditing(true);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setEditedJob(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSaveJob = async () => {
    try {
      const jobRef = doc(db, 'jobs', selectedJob.id);
      await updateDoc(jobRef, {
        title: editedJob.title,
        company: editedJob.company,
        description: editedJob.description,
        requirements: editedJob.requirements,
        location: editedJob.location,
        salary: editedJob.salary,
        reasonsToJoin: editedJob.reasonsToJoin
      });

      // Update the job in the local state
      setJobPostings(prev => 
        prev.map(job => 
          job.id === selectedJob.id ? { ...job, ...editedJob } : job
        )
      );

      setSelectedJob(prev => ({ ...prev, ...editedJob }));
      setIsEditing(false);
    } catch (error) {
      console.error('Error updating job:', error);
    }
  };

  const handleDeleteJob = async () => {
    if (!window.confirm('Are you sure you want to delete this job posting? This action cannot be undone.')) {
      return;
    }

    setIsDeleting(true);
    try {
      // Delete the job document
      await deleteDoc(doc(db, 'jobs', selectedJob.id));
      
      // Remove the job from the local state
      setJobPostings(prev => prev.filter(job => job.id !== selectedJob.id));
      
      // Close the modal
      closeJobModal();
    } catch (error) {
      console.error('Error deleting job:', error);
    } finally {
      setIsDeleting(false);
    }
  };

  if (loading) {
    return <div className="applicants-page">Loading...</div>;
  }

  return (
    <div className="applicants-page">
      <div className="applicants-header">
        <h2 className="applicants-title" style={{ fontSize: '1.35rem', fontWeight: 600, color: 'var(--primary-color)', letterSpacing: 0, marginBottom: 12, textAlign: 'center', width: '100%' }}>Active Job Listings</h2>
      </div>
      <div className="search-container">
        <input
          type="text"
          className="search-input"
          placeholder="Search applicants by name, job, or resume..."
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
        />
      </div>
      <div className="applicants-content">
        {filteredJobPostings.length === 0 ? (
          <div className="no-applicants-message">
            <div className="no-applicants-icon">📋</div>
            <div className="no-applicants-title">No Job Postings Yet</div>
            <div className="no-applicants-text">
              Start posting jobs to receive applications from qualified candidates.
            </div>
          </div>
        ) : (
          <div className="job-postings-list">
            {filteredJobPostings.map((job) => (
          <div key={job.id} className="job-posting">
                <div 
                  className="job-posting-header" 
                  onClick={() => toggleJobExpansion(job.id)}
                >
                  <h2 className="job-title">{job.title}</h2>
                  <div className="job-meta">
                    <span className="applicant-count">
                      {job.applications.length} {job.applications.length === 1 ? 'applicant' : 'applicants'}
                    </span>
                    <span className={`expand-icon ${expandedJobs.has(job.id) ? 'expanded' : ''}`}>
                      ▼
                    </span>
                  </div>
                </div>
                
                {expandedJobs.has(job.id) && (
                  <div className="applicants-dropdown">
                    <div className="job-actions">
                      <button 
                        className="view-job-details-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          openJobDetails(job);
                        }}
                      >
                        View Job Details
                      </button>
                    </div>
                    {job.applications.length === 0 ? (
                      <div className="no-applicants-for-job">
                        No applicants for this job yet.
                      </div>
                    ) : (
                      <div className="applicants-card-list">
                        {job.applications.map((application) => (
                          <div key={application.id} className="applicant-card">
                            <div className="applicant-card-header">
                              <span className="applicant-name">{application.employeeName}</span>
                              <span className={`status-label status-${application.status}`}>{application.status}</span>
                              {conversationEmployeeIds.has(application.employeeId) && (
                                <span className="conversation-badge">Conversation Started</span>
                              )}
                            </div>
                            <div className="applicant-card-body">
                              <div className="applicant-info-row">
                                <span className="info-label">Resume:</span>
                                {application.resumeUrl ? (
                                  <a
                                    href={application.resumeUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="view-resume-link"
                                  >
                      View Resume
                    </a>
                                ) : (
                                  <span className="no-resume">No resume</span>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Job Details Modal */}
      {showJobModal && selectedJob && (
        <div className="modal-overlay" onClick={closeJobModal}>
          <div className="job-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>JOB DETAILS</h2>
              <button className="close-modal-btn" onClick={closeJobModal}>&times;</button>
            </div>
            <div className="job-meta-info">
              <div className="job-meta-row"><span className="job-meta-label">Title:</span><span className="job-meta-value">{selectedJob.title}</span></div>
              <div className="job-meta-row"><span className="job-meta-label">Company:</span><span className="job-meta-value">{selectedJob.company}</span></div>
              <div className="job-meta-row"><span className="job-meta-label">Location:</span><span className="job-meta-value">{selectedJob.location}</span></div>
              <div className="job-meta-row"><span className="job-meta-label">Salary:</span><span className="job-meta-value">{selectedJob.salary}</span></div>
            </div>
            <div className="modal-section">
              <div className="modal-section-title">Description</div>
              <div className="modal-section-content">{selectedJob.description || 'No description provided.'}</div>
            </div>
            <div className="modal-section">
              <div className="modal-section-title">Requirements</div>
              <div className="modal-section-content">{selectedJob.requirements || 'No requirements listed.'}</div>
            </div>
            <div className="modal-section">
              <div className="modal-section-title">Reasons to Join</div>
              <div className="modal-section-content">
                {Array.isArray(selectedJob.reasonsToJoin) && selectedJob.reasonsToJoin.length > 0 ? (
                  <ul>
                    {selectedJob.reasonsToJoin.map((reason, i) => <li key={i}>{reason}</li>)}
                  </ul>
                ) : (
                  <span>{selectedJob.reasonsToJoin || 'No reasons listed.'}</span>
                )}
              </div>
            </div>
            <div className="modal-footer">
              <button className="delete-btn" onClick={handleDeleteJob}>Delete Job</button>
              <button className="edit-btn" onClick={handleEditClick}>Edit Job</button>
            </div>
          </div>
        </div>
      )}
      {/* Floating Post Job Button */}
      <button
        onClick={() => navigate('/add-job')}
        style={{
          position: 'fixed',
          right: 28,
          bottom: 80,
          zIndex: 900,
          background: 'var(--primary-color)',
          color: 'var(--tab-active)',
          border: 'none',
          borderRadius: 18,
          padding: '14px 28px',
          fontWeight: 700,
          fontSize: 16,
          boxShadow: '0 4px 16px var(--shadow-color)',
          cursor: 'pointer',
          transition: 'background 0.18s, box-shadow 0.18s',
        }}
      >
        Post Job
      </button>
    </div>
  );
};

export default Applicants;
